/* eslint-disable @typescript-eslint/no-explicit-any */
import { TOptimizelyMeta } from './OptimizelyMeta';

const ERROR_MESSAGES = {
    INSTANCE: 'Unable to track Web Experimentation events: optimizely not available on window object.',
    PURCHASE: 'Purchase event not triggered: order object or totals unavailable.',
    ADD_TO_CART: 'Add to cart event not triggered: price object unavailable.'
};

/**
 * @description Base OptimizelyWebExpMeta implementation
 * @param OptimizelyMeta Base widget for extending
 * @returns OptimizelyWebExpMeta class
 */
function OptimizelyWebExpMetaClassCreator(OptimizelyMeta: TOptimizelyMeta) {
    class OptimizelyWebExpMeta extends OptimizelyMeta {
        eventType = 'event';

        optimizely = window.optimizely;

        enableTracking?: boolean;

        analyticsExpType: string = 'web_exp';

        EVENTS = {
            ...this.EVENTS,
            MEMBERSHIP: 'membership_registration',
            ADD_TO_CART: 'add_to_cart', // web and feature exp differ in this ev key
            PURCHASE: 'purchase' // web and feature exp differ in this ev key
        };

        prefs() {
            return {
                isTrackingAllowed: false,
                orderTotal: 0,
                ...super.prefs()
            };
        }

        /**
         * Initializes class properties and events.
         */
        initInstance() {
            if (this.initialized) {
                return;
            }

            this.optimizely = window.optimizely?.initialized ? window.optimizely : this.optimizely;
            this.enableTracking = this.isConsentGiven && this.prefs().isTrackingAllowed;

            if (!this.optimizely) {
                this.logWarn(ERROR_MESSAGES.INSTANCE);

                return;
            }

            this.sendTrackingEventsQueued();

            this.eventBus().on('account.signup', 'accountRegistration');

            if (window.contexts.includes('pdp')) {
                this.trackEvent(this.EVENTS.PDP);
            }

            if (window.contexts.includes('orderconfirmation')) {
                this.orderSubmitted();
            }

            this.initialized = true;
        }

        /**
         * Tracks the account registration event.
         */
        accountRegistration(): void {
            this.trackEvent(this.EVENTS.MEMBERSHIP);
        }

        /**
         * Tracks a 'purchase' event.
         *
         */
        orderSubmitted(): void {
            const orderTotal = this.prefs().orderTotal;

            if (!orderTotal) {
                this.logWarn(ERROR_MESSAGES.PURCHASE);

                return;
            }

            /* Revenue tag must not be sent as decimal. */
            const data = {
                revenue: (orderTotal || 0) * 100
            };

            this.trackEvent(this.EVENTS.PURCHASE, data);
        }

        /**
         * Sends tracking event to Optimizely Web Experimentation via 'push' API.
         *
         * @param eventName - The name of the event to track.
         * @param tags - Optional additional data to include with the event, i.e. 'tags'
         * or 'revenue'.
         */
        trackEvent(eventName: string, tags?: any): void {
            if (!this.enableTracking) {
                return;
            }

            if (!this.optimizely) {
                this.logWarn(ERROR_MESSAGES.INSTANCE);

                return;
            }

            const event = {
                type: this.eventType,
                eventName,
                tags
            };

            this.optimizely?.push(event);
        }

        /**
         * Triggers analytics tracking for the campaign decisions
         * made for a user in the page.
         */
        sendTrackingEventsQueued() {
            const queue = window.optimizelyEventsQueue;

            if (!queue || !queue.length) {
                return;
            }

            while (queue.length) {
                const eventJSON = queue.pop() || '{}';
                const event = JSON.parse(eventJSON);

                this.trackAnalyticsEvent(event);
            }
        }
    }

    return OptimizelyWebExpMeta;
}

export type TOptimizelyWebExpMeta = ReturnType<typeof OptimizelyWebExpMetaClassCreator>;

export type TOptimizelyWebExpMetaInstance = InstanceType<TOptimizelyWebExpMeta>;

export default OptimizelyWebExpMetaClassCreator;
